import { z } from "zod";

export const marketJobIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const marketJobStatusSchema = z.enum(["OPEN", "OFFERED", "BOOKED"]);
export const marketJobBookingModeSchema = z.enum(["FIXED_ACCEPT", "NEGOTIABLE"]);
export const marketJobCurrencySchema = z.literal("USD");
export const marketJobCleaningTypeSchema = z.enum([
  "STANDARD",
  "DEEP",
  "MOVE_IN",
  "MOVE_OUT",
  "TURNOVER",
  "CUSTOM",
]);
export const marketJobPropertyTypeSchema = z.enum([
  "HOUSE",
  "APARTMENT",
  "CONDO",
  "TOWNHOUSE",
  "OTHER",
]);
export const marketJobPricingUnitSchema = z.enum([
  "JOB",
  "HOUR",
  "ROOM",
  "BEDROOM",
  "BATHROOM",
  "SQFT",
  "ITEM",
]);

const isoDateTimeSchema = z.string().datetime({ offset: true });
const timezoneRegionSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone: value }).format();
      return true;
    } catch {
      return false;
    }
  }, "Use a valid timezone region");
const bedroomCountSchema = z.number().int().min(0).max(999);
const bathroomCountSchema = z.number().min(0).max(999.5).multipleOf(0.5);
const roomCountSchema = z.number().int().min(0).max(9_999).nullable();
const sizeSqftSchema = z.number().int().positive().max(99_999_999).nullable();
const fixedPriceSchema = z.number().int().min(100).max(1_000_000);

const marketJobCommonFields = {
  id: marketJobIdSchema,
  jobNumber: z.string().regex(/^WBJ-[0-9A-F]{26}$/u),
  cleaningType: marketJobCleaningTypeSchema,
  currency: marketJobCurrencySchema,
  requestStartAt: isoDateTimeSchema,
  requestEndAt: isoDateTimeSchema,
  timezoneRegion: timezoneRegionSchema,
  estimatedDurationMinutes: z.number().int().positive(),
  postedAt: isoDateTimeSchema,
  propertyType: marketJobPropertyTypeSchema,
  bedroomCount: bedroomCountSchema,
  bathroomCount: bathroomCountSchema,
  roomCount: roomCountSchema,
  sizeSqft: sizeSqftSchema,
  locality: z.string().min(1).max(100),
  regionCode: z.string().min(1).max(100),
  postalCode: z.string().min(1).max(30),
  countryCode: z.string().regex(/^[A-Z]{2}$/u),
  serviceCount: z.number().int().min(1).max(100),
} as const;

function validateRequestWindow(
  job: { requestStartAt: string; requestEndAt: string },
  context: z.RefinementCtx,
): void {
  if (Date.parse(job.requestEndAt) <= Date.parse(job.requestStartAt)) {
    context.addIssue({
      code: "custom",
      path: ["requestEndAt"],
      message: "Request end must follow request start",
    });
  }
}

const fixedMarketJobSummarySchema = z.strictObject({
  ...marketJobCommonFields,
  jobStatus: z.literal("OPEN"),
  bookingMode: z.literal("FIXED_ACCEPT"),
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
});
const negotiableMarketJobSummarySchema = z.strictObject({
  ...marketJobCommonFields,
  jobStatus: z.enum(["OPEN", "OFFERED"]),
  bookingMode: z.literal("NEGOTIABLE"),
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
});
export const marketJobSummarySchema = z
  .discriminatedUnion("bookingMode", [
    fixedMarketJobSummarySchema,
    negotiableMarketJobSummarySchema,
  ])
  .superRefine(validateRequestWindow);

export const marketJobServiceSchema = z.strictObject({
  serviceId: marketJobIdSchema,
  serviceCode: z.string().min(1).max(100),
  serviceName: z.string().min(1).max(200),
  pricingUnit: marketJobPricingUnitSchema,
  quantity: z.number().int().min(1).max(100),
});

const marketJobDetailFields = {
  services: z.array(marketJobServiceSchema).min(1).max(100),
} as const;
const fixedMarketJobDetailSchema = z.strictObject({
  ...marketJobCommonFields,
  jobStatus: z.literal("OPEN"),
  bookingMode: z.literal("FIXED_ACCEPT"),
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
  ...marketJobDetailFields,
});
const negotiableMarketJobDetailSchema = z.strictObject({
  ...marketJobCommonFields,
  jobStatus: marketJobStatusSchema,
  bookingMode: z.literal("NEGOTIABLE"),
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
  ...marketJobDetailFields,
});

function validateMarketJobDetail(
  job: z.infer<typeof fixedMarketJobDetailSchema> | z.infer<typeof negotiableMarketJobDetailSchema>,
  context: z.RefinementCtx,
): void {
  validateRequestWindow(job, context);
  if (job.services.length !== job.serviceCount) {
    context.addIssue({
      code: "custom",
      path: ["services"],
      message: "Service count must match the service list",
    });
  }

  const serviceIds = new Set<string>();
  job.services.forEach(({ serviceId }, index) => {
    if (serviceIds.has(serviceId)) {
      context.addIssue({
        code: "custom",
        path: ["services", index, "serviceId"],
        message: "Service IDs must be unique",
      });
    }
    serviceIds.add(serviceId);
  });
}

export const marketJobDetailSchema = z
  .discriminatedUnion("bookingMode", [fixedMarketJobDetailSchema, negotiableMarketJobDetailSchema])
  .superRefine(validateMarketJobDetail);

export const marketJobListResponseSchema = z.strictObject({
  status: z.literal("ok"),
  jobs: z.array(marketJobSummarySchema).max(50),
});

export const marketJobResponseSchema = z.strictObject({
  status: z.literal("ok"),
  job: marketJobDetailSchema,
});

export type MarketJobStatus = z.infer<typeof marketJobStatusSchema>;
export type MarketJobBookingMode = z.infer<typeof marketJobBookingModeSchema>;
export type MarketJobCleaningType = z.infer<typeof marketJobCleaningTypeSchema>;
export type MarketJobPropertyType = z.infer<typeof marketJobPropertyTypeSchema>;
export type MarketJobPricingUnit = z.infer<typeof marketJobPricingUnitSchema>;
export type MarketJobSummary = z.infer<typeof marketJobSummarySchema>;
export type MarketJobService = z.infer<typeof marketJobServiceSchema>;
export type MarketJobDetail = z.infer<typeof marketJobDetailSchema>;
